/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState } from "react";
import "./ProjectCard.css";
import { stringToProjectImage } from "../../services/projectImageService";
import { techLogoToPicture } from "../../services/techLogoService";
import Tooltip from "../tooltip/Tooltip";

interface ProjectCardProps {
    name: string;
    description: string;
    image: string;
    techLogos: string[];
}

const ProjectCard: React.FC<ProjectCardProps> = ({
    name,
    description,
    image,
    techLogos,
}) => {
    const [showTechTooltip, setShowTechTooltip] = useState(false);
    const [techTooltipPosition, setTechTooltipPosition] = useState({
        x: 0,
        y: 0,
    });
    const [activeTechLogo, setActiveTechLogo] = useState<string>("");
    const [showCardTooltip, setShowCardTooltip] = useState(false);
    const [cardTooltipPosition, setCardTooltipPosition] = useState({
        x: 0,
        y: 0,
    });
    const [isOverTechContainer, setIsOverTechContainer] = useState(false);

    const isMobile = window.innerWidth <= 600;

    return (
        <div
            className="project-card-container"
            onMouseEnter={(e) => {
                if (!isMobile) {
                    if (!isOverTechContainer && !showTechTooltip) {
                        setShowCardTooltip(true);
                    }
                    setCardTooltipPosition({
                        x: e.clientX,
                        y: e.clientY + 20,
                    });
                }
            }}
            onMouseLeave={() => {
                if (!isMobile) {
                    setShowCardTooltip(false);
                }
            }}
            onMouseMove={(e) => {
                if (!isMobile && !isOverTechContainer && !showTechTooltip) {
                    setCardTooltipPosition({
                        x: e.clientX,
                        y: e.clientY + 20,
                    });

                    // Make sure the card tooltip is showing if we're moving over the card
                    // but not over tech container or specific tech
                    if (!showCardTooltip) {
                        setShowCardTooltip(true);
                    }
                }
            }}
        >
            {showCardTooltip &&
                !showTechTooltip &&
                !isOverTechContainer &&
                !isMobile && (
                    <Tooltip
                        text="Click to learn more"
                        position={cardTooltipPosition}
                    />
                )}
            <div className="project-card-image-container">
                <img
                    src={stringToProjectImage(image)}
                    alt="Project Image"
                    className="project-card-image"
                />
            </div>
            <div className="project-card-title-and-technologies-container">
                <p className="project-card-title">{name}</p>
                <div
                    className="project-card-technologies-container"
                    onMouseEnter={() => {
                        if (!isMobile) {
                            setIsOverTechContainer(true);
                            setShowCardTooltip(false);
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isMobile) {
                            setIsOverTechContainer(false);

                            // Check if we're still over the card by getting current mouse position
                            const cardElement = e.currentTarget.closest(
                                ".project-card-container"
                            );
                            if (cardElement) {
                                const rect =
                                    cardElement.getBoundingClientRect();
                                if (
                                    e.clientX >= rect.left &&
                                    e.clientX <= rect.right &&
                                    e.clientY >= rect.top &&
                                    e.clientY <= rect.bottom
                                ) {
                                    setShowCardTooltip(true);
                                }
                            }
                        }
                    }}
                >
                    {techLogos.map((logo) => (
                        <div
                            key={logo}
                            onMouseEnter={(e) => {
                                if (!isMobile) {
                                    setShowTechTooltip(true);
                                    setActiveTechLogo(logo);
                                    setShowCardTooltip(false);
                                    setTechTooltipPosition({
                                        x: e.clientX,
                                        y: e.clientY + 20,
                                    });
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isMobile) {
                                    setShowTechTooltip(false);
                                    setActiveTechLogo("");

                                    // If we're still over the card but not over the tech container,
                                    // we should show the card tooltip
                                    if (!isOverTechContainer) {
                                        setShowCardTooltip(true);
                                    }
                                }
                            }}
                            onMouseMove={(e) => {
                                if (!isMobile) {
                                    setTechTooltipPosition({
                                        x: e.clientX,
                                        y: e.clientY + 20,
                                    });
                                }
                            }}
                        >
                            <img
                                src={techLogoToPicture(logo)}
                                alt={`${logo}`}
                                className="technologies-logo"
                            />
                            {showTechTooltip &&
                                activeTechLogo === logo &&
                                !isMobile && (
                                    <Tooltip
                                        text={
                                            logo.charAt(0).toUpperCase() +
                                            logo.slice(1)
                                        }
                                        position={techTooltipPosition}
                                    />
                                )}
                        </div>
                    ))}
                </div>
            </div>
            <div className="project-card-description-container">
                <p className="project-card-description">{description}</p>
            </div>
        </div>
    );
};

export default ProjectCard;
